import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  Download,
  Add,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import { exportService } from '../../services/exportService'
import PageHeader from '../PageHeader'

const ExportJobs = () => {
  const { groupId } = useParams()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [creating, setCreating] = useState(false)

  // Fetch jobs on mount and set up polling
  useEffect(() => {
    fetchJobs()
    const interval = setInterval(() => {
      fetchJobs(true)
    }, 5000) // Poll every 5 seconds

    return () => clearInterval(interval)
  }, [groupId])

  const fetchJobs = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const jobsData = await exportService.getExportHistory(groupId, 20)
      setJobs(jobsData || [])
    } catch (error) {
      console.error('Error fetching jobs:', error)
      if (!silent) {
        toast.error('Failed to load export history')
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const handleCreateExport = async () => {
    if (startDate && endDate) {
      if (startDate > endDate) {
        toast.error('Start date cannot be after end date')
        return
      }
    }

    setCreating(true)
    try {
      // Format dates to YYYY-MM-DD if they exist
      const formattedStartDate = startDate
        ? startDate.toISOString().split('T')[0]
        : null
      const formattedEndDate = endDate
        ? endDate.toISOString().split('T')[0]
        : null

      await exportService.startExport(
        groupId,
        formattedStartDate,
        formattedEndDate
      )

      toast.success('Export job started successfully')
      setDialogOpen(false)
      setStartDate(null)
      setEndDate(null)

      // Refresh the list immediately
      fetchJobs()
    } catch (error) {
      console.error('Error creating export:', error)
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export data')
      } else {
        toast.error('Failed to start export. Please try again.')
      }
    } finally {
      setCreating(false)
    }
  }

  const handleDownload = async (job) => {
    try {
      // Get fresh status to get a new signed URL
      const status = await exportService.getJobStatus(groupId, job.id)
      if (status.downloadUrl) {
        exportService.downloadFromUrl(
          status.downloadUrl,
          `group-${groupId}-export-${job.id}.csv`
        )
        toast.success('Download started')
      } else {
        toast.error('Download URL not available')
      }
    } catch (error) {
      console.error('Error downloading:', error)
      toast.error('Failed to download export')
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color='success' />
      case 'failed':
        return <ErrorIcon color='error' />
      case 'processing':
      case 'pending':
        return <HourglassEmpty color='primary' />
      default:
        return <HourglassEmpty />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'failed':
        return 'error'
      case 'processing':
        return 'primary'
      case 'pending':
        return 'default'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  return (
    <div className='h-full'>
      <PageHeader title='Data Export' subtitle='Export group data to CSV' />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='body1' color='text.secondary'>
          Create and manage data export jobs. Exports are processed asynchronously and can be downloaded when ready.
        </Typography>
        <Box>
          <Tooltip title='Refresh'>
            <IconButton onClick={() => fetchJobs()} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant='contained'
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            New Export
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant='body1' color='text.secondary' align='center'>
              No export jobs found. Create your first export to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {jobs.map((job) => (
            <Card key={job.id} variant='outlined'>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {getStatusIcon(job.status)}
                      <Typography variant='h6'>
                        Export {job.id.substring(0, 8)}...
                      </Typography>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status)}
                        size='small'
                      />
                    </Box>

                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                      Date Range: {formatDate(job.startDate)} to {formatDate(job.endDate)}
                    </Typography>

                    <Typography variant='body2' color='text.secondary'>
                      Created: {formatDateTime(job.createdAt)}
                      {job.completedAt && ` • Completed: ${formatDateTime(job.completedAt)}`}
                    </Typography>

                    {job.status === 'processing' && typeof job.progress === 'number' && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress variant='determinate' value={job.progress} />
                        <Typography variant='caption' color='text.secondary'>
                          {job.progress}% complete
                        </Typography>
                      </Box>
                    )}

                    {job.status === 'failed' && job.error && (
                      <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                        Error: {job.error}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    {job.status === 'completed' && (
                      <Button
                        variant='contained'
                        startIcon={<Download />}
                        onClick={() => handleDownload(job)}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create Export Dialog */}
      <Dialog open={dialogOpen} onClose={() => !creating && setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Create New Export</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Create a new export job. The data will be processed asynchronously and you'll be able to download it when ready.
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label='Start Date'
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                disabled={creating}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <DatePicker
                label='End Date'
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                disabled={creating}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>

            {!startDate && !endDate && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 2, display: 'block' }}>
                Leave dates empty to export all data
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateExport}
            variant='contained'
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <Add />}
          >
            {creating ? 'Creating...' : 'Create Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ExportJobs
